import React, { createContext, useCallback, useContext, useMemo, useState } from "react";
import type { Product } from "../types/product";
export interface CartItem {
  key: string;
  product: Product;
  color?: string;
  note?: string;
  qty: number;
}
interface CartContextValue {
  items: CartItem[];
  count: number;
  subtotal: number;
  isOpen: boolean;
  open: () => void;
  close: () => void;
  add: (product: Product, options?: {
    color?: string;
    qty?: number;
    note?: string;
    silent?: boolean;
  }) => void;
  updateQty: (key: string, qty: number) => void;
  remove: (key: string) => void;
  clear: () => void;
  wishlist: string[];
  toggleWish: (id: string) => void;
}
const CartContext = createContext<CartContextValue | null>(null);
export function CartProvider({
  children
}: {
  children: React.ReactNode;
}) {
  const [items, setItems] = useState<CartItem[]>([]);
  const [isOpen, setIsOpen] = useState(false);
  const [wishlist, setWishlist] = useState<string[]>([]);
  const add = useCallback<CartContextValue["add"]>((product, options = {}) => {
    const {
      color,
      qty = 1,
      note,
      silent
    } = options;
    const key = `${product.id}-${color ?? "default"}-${note ?? ""}`;
    setItems(prev => {
      const existing = prev.find(i => i.key === key);
      if (existing) return prev.map(i => i.key === key ? {
        ...i,
        qty: i.qty + qty
      } : i);
      return [...prev, {
        key,
        product,
        color,
        note,
        qty
      }];
    });
    if (!silent) setIsOpen(true);
  }, []);
  const updateQty = useCallback((key: string, qty: number) => {
    setItems(prev => qty <= 0 ? prev.filter(i => i.key !== key) : prev.map(i => i.key === key ? {
      ...i,
      qty
    } : i));
  }, []);
  const remove = useCallback((key: string) => setItems(prev => prev.filter(i => i.key !== key)), []);
  const clear = useCallback(() => setItems([]), []);
  const toggleWish = useCallback((id: string) => {
    setWishlist(prev => prev.includes(id) ? prev.filter(w => w !== id) : [...prev, id]);
  }, []);
  const value = useMemo<CartContextValue>(() => ({
    items,
    count: items.reduce((sum, i) => sum + i.qty, 0),
    subtotal: items.reduce((sum, i) => sum + i.qty * i.product.price, 0),
    isOpen,
    open: () => setIsOpen(true),
    close: () => setIsOpen(false),
    add,
    updateQty,
    remove,
    clear,
    wishlist,
    toggleWish
  }), [items, isOpen, add, updateQty, remove, clear, wishlist, toggleWish]);
  return <CartContext.Provider value={value} data-id="element-451" data-mp="fx2e3t0zms4h">{children}</CartContext.Provider>;
}
export function useCart(): CartContextValue {
  const ctx = useContext(CartContext);
  if (!ctx) throw new Error("useCart must be used within CartProvider");
  return ctx;
}